import React, { useContext, useState } from 'react';
import { MainContext } from '../../context/MainContext';

import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import * as Constants from '../api/constants';

let deviceId = 0;

export default function DashboardMeter() {
  const [voltage, setVoltage] = useState();
  const [current, setCurrent] = useState();

  const state = useContext(MainContext).state;
  deviceId = state.selectedDevice;

  const r2 = state.R2.split(',');
  const r3 = state.R3.split(',');
  const r4 = state.R4.split(',');
  const r7 = state.R7.split(',');

  const mains = [
    { label: 'Voltage', value: `${r2[2]} V` },
    { label: 'Current', value: `${(r2[1] / 10.0).toFixed(1)} A` },
    { label: 'Wattage', value: `${(r4[10] / 10.0).toFixed(1)} W` },
  ];

  const other = [
    { label: 'Current 0 count', value: r7[15] },
    { label: 'Current raw count', value: r3[19] },
    { label: '12V port current', value: `${r2[4]} mA` },
    { label: 'Case temp', value: `${r2[3]} °C` },
  ];

  const voltageOffset = (r7[17] / 10.0).toFixed(1);
  const currentOffset = (r7[16] / 10.0).toFixed(1);

  const sendCommand = (command, value) => {
    const requestOptions = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        DeviceId: deviceId,
        Command: command,
        Value: value.toString(),
      }),
      credentials: 'include',
    };

    fetch(Constants.baseURL + Constants.apiSendCommand, requestOptions)
      .then((response) => {
        if (response.ok) {
          toast.success('Command sent successfully. Refreshing...', {
            position: 'top-right',
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });

          setTimeout(function () {
            state.refreshDeviceRF(deviceId);
          }, 6000);

          setTimeout(function () {
            state.getUserDeviceRf(deviceId);
          }, 12000);
        } else {
          toast.error('Send failed. Please try again', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }
      })
      .catch((error) => {
        console.log('request failed', error);
      });
  };

  const VoltageSendButton = () => (
    <Button
      size="small"
      variant="text"
      sx={{ color: 'white' }}
      onClick={() => sendCommand('VoltageAdjustment', voltage ?? '')}
    >
      Send
    </Button>
  );

  const CurrentSendButton = () => (
    <Button
      size="small"
      variant="text"
      sx={{ color: 'white' }}
      onClick={() => sendCommand('CurrentAdjustment', current ?? '')}
    >
      Send
    </Button>
  );

  const ICON_COLORS = {
    power: "#FFD54F",       // electrical
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <Typography
        component="h2"
        variant="h6"
        color="white"
        gutterBottom
        sx={{ fontWeight: 600 }}
      >
        Meter
      </Typography>

      <Container
        disableGutters
        maxWidth="lg"
        sx={{ flexDirection: 'row', px: 0 }}
      >
        <Grid container spacing={3}>
          {/* Mains */}
          <Grid item xs={12} md={4}>
            <Typography
              sx={{ color: 'white', fontWeight: 600, mb: 1 }}
            >
              Mains
            </Typography>
            <Grid container spacing={1.5}>
              {mains.map((item) => (
                <Grid item xs={12} key={item.label}>
                  <Paper
                    sx={{
                      p: 1.4,
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.4,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      transition: '0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        bgcolor: 'rgba(255,255,255,0.08)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255,255,255,0.12)',
                        color: 'rgba(255,255,255,0.9)',
                        flexShrink: 0,
                      }}
                    >
                      <BoltOutlinedIcon fontSize="small" sx={{ color: ICON_COLORS.power }} />
                    </Box>
                    <Box sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: 0.3,
                      mt: 0.4
                    }}>
                      <Typography
                        variant="subtitle2"
                        color="white"
                        sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1, }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.disabled',
                          fontSize: 13,
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Other + Zero Amps */}
          <Grid item xs={12} md={4}>
            <Typography
              sx={{ color: 'white', fontWeight: 600, mb: 1 }}
            >
              Other
            </Typography>
            <Grid container spacing={1.5}>
              {other.map((item) => (
                <Grid item xs={12} key={item.label}>
                  <Paper
                    sx={{
                      p: 1.4,
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.4,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      transition: '0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        bgcolor: 'rgba(255,255,255,0.08)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255,255,255,0.12)',
                        color: 'rgba(255,255,255,0.9)',
                        flexShrink: 0,
                      }}
                    >
                      <BoltOutlinedIcon fontSize="small" sx={{ color: ICON_COLORS.power }} />
                    </Box>
                    <Box sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: 0.3,
                      mt: 0.4
                    }}>
                      <Typography
                        color="white"
                        sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1 }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.disabled',
                          fontSize: 13,
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    width: '100%',
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.4)',
                    mt: 1,
                  }}
                  onClick={() => sendCommand('ZeroAmps', '')}
                >
                  Zero Amps
                </Button>
              </Grid>
            </Grid>
          </Grid>

          {/* Voltage / Current Adjust */}
          <Grid item xs={12} md={4}>
            {/* Voltage card */}
            <Typography
              sx={{ color: 'white', fontWeight: 600, mb: 1 }}
            >
              Voltage
            </Typography>
            <Paper
              sx={{
                p: 1.4,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                mb: 1.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Box>
                  <Typography
                    color="white"
                    sx={{ fontSize: 14, fontWeight: 700 }}
                  >
                    Calibration
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    {voltageOffset}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      minWidth: 32,
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.6)',
                    }}
                    onClick={() => sendCommand('VoltageDec', '')}
                  >
                    –
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      minWidth: 32,
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.6)',
                    }}
                    onClick={() => sendCommand('VoltageInc', '')}
                  >
                    +
                  </Button>
                </Box>
              </Box>

              <TextField
                fullWidth
                onChange={(event) => {
                  setVoltage(event.target.value);
                }}
                variant="outlined"
                size="small"
                InputProps={{
                  endAdornment: <VoltageSendButton />,
                  sx: {
                    color: 'white',
                  },
                }}
                sx={{
                  mt: 0.5,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,255,255,0.6)',
                    },
                  },
                  '& input': { color: 'white' },
                }}
              />
            </Paper>

            {/* Current card */}
            <Typography
              sx={{ color: 'white', fontWeight: 600, mb: 1, mt: 1 }}
            >
              Current
            </Typography>
            <Paper
              sx={{
                p: 1.4,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="white"
                    sx={{ fontSize: 14, fontWeight: 700 }}
                  >
                    Calibration
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                      fontSize: 13,
                    }}
                  >
                    {currentOffset}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      minWidth: 32,
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.6)',
                    }}
                    onClick={() => sendCommand('CurrentDec', '')}
                  >
                    –
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      minWidth: 32,
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.6)',
                    }}
                    onClick={() => sendCommand('CurrentInc', '')}
                  >
                    +
                  </Button>
                </Box>
              </Box>

              <TextField
                fullWidth
                onChange={(event) => {
                  setCurrent(event.target.value);
                }}
                variant="outlined"
                size="small"
                InputProps={{
                  endAdornment: <CurrentSendButton />,
                  sx: {
                    color: 'white',
                  },
                }}
                sx={{
                  mt: 0.5,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,255,255,0.6)',
                    },
                  },
                  '& input': { color: 'white' },
                }}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </React.Fragment>
  );
}
